import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { ErrorIcon, Input, Loader, ProviderConsumer, SearchIcon } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { useTranslation } from 'react-i18next';
import { AppStateValue, appState } from '../AppState';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { EmptyData } from '../shared/components/EmptyData';
import { ResultCount } from '../shared/components/ResultCount';
import { SkeletonTable } from '../shared/components/Skeleton';
import { TicketCardSkeleton } from '../shared/components/TicketCard/TicketCardSkeleton';
import ClassDetailList from '../shared/components/table/ClassDetailList';
import { ListPreference, listPreferenceKey, readListPreference, writeListPreference } from '../shared/components/table/listPreferences';
import { usePersistedSort } from '../shared/components/table/usePersistedSort';
import { useListPreferenceFlag } from '../shared/components/table/useListPreferenceFlag';
import { cardListPageSize } from '../shared/utils/phonePaging';
import { onNextUserGesture } from '../shared/utils/nextUserGesture';
import { appendPage } from '../tikit/MyWork/MyWorkBoard/lanePaging';
import { ListViewBar } from '../shared/components/table/ListViewBar';
import { TablePagination } from '../shared/components/table/TablePagination';
import { toTicketRow } from '../shared/components/table/TicketListBoard';
import { TicketProps } from '../shared/interfaces/ticketproperties.interface';
import { ICustomProperty } from '../shared/interfaces/customproperties.interface';
import { platformService } from '../shared/services/platform.service';
import { graphService } from '../shared/services/graph.service';
import { getExpandedTeamMembers } from '../tikit/ticketHelper';
import { ConfigurationName, FILTERS_STRINGS, LICENSE_FEATURE, ListColumnKey, NON_SORTABLE_COLUMNS, TICKET_LIST_COLUMN_IDS, TICKET_PAGINATION } from '../shared/utils/constants';
import { attachTicketSlas, attachTicketUsers, getCustomPropertyQueryValue } from '../tikit/List/ListFunctions';
import { EnumSortOrderType } from '../tikit/toolbar/CustomViewContextProvider';
import { buildUserTicketsQuery } from './userTicketsQuery';
import { getMobileTicketProperties } from '../tikit/mobile/mobileTicketProperties';
import { TicketDetailCard } from '../tikit/EditTicket/TicketDetailCard';
import useWindowWidth from '../tikit/useWindowWidth';
import EmptySVG from '../../svg/empty.svg';
import FlashlightSVG from '../../svg/flashlight.svg';
import AppCSS from '../tikit/Tikit.module.css';
import { keepUnchangedSlas } from '../shared/utils/slaCarryOver';
import { liveWithArchived } from '../shared/utils/archivedLookups';

// The core ticketing columns, in the order the ticket list's default views draw them. Type, SLA, Team,
// Template and Lifecycle/Phase still drop out for a tenant without the feature, as they do in the list.
const COLUMNS: number[] = [
  ListColumnKey.Id,
  ListColumnKey.Type,
  ListColumnKey.Title,
  ListColumnKey.Status,
  ListColumnKey.GroupChat,
  ListColumnKey.Requester,
  ListColumnKey.Category,
  ListColumnKey.Priority,
  ListColumnKey.SLA,
  ListColumnKey.Team,
  ListColumnKey.Group,
  ListColumnKey.Assignee,
  ListColumnKey.Tags,
  ListColumnKey.Template,
  ListColumnKey.Lifecycle,
  ListColumnKey.Phase,
  ListColumnKey.TotalAgentTime,
  ListColumnKey.Source,
  ListColumnKey.DueDate,
  ListColumnKey.LastModifiedDate,
  ListColumnKey.CreatedDate,
  ListColumnKey.ResolutionDate,
  ListColumnKey.ResolutionCategory,
  ListColumnKey.Note,
  ListColumnKey.InternalNote,
  ListColumnKey.DraftKB,
];

// After the fixed columns, every custom property the ticket list shows, in their Settings order - keyed as there.
const withCustomPropertyColumns = (customProperties: ICustomProperty[] | undefined) => [
  ...COLUMNS,
  ...(customProperties ?? []).filter(p => !p.IsHidden).sort((a, b) => a.Order - b.Order).map(p => 1000 + p.Id),
];

const DEFAULT_SORT: ISortOrder = { orderBy: ListColumnKey.Id, orderType: EnumSortOrderType.desc };

// One layout for the Tickets tab on every user's page - it is the same table each time.
const userTicketsKey = (preference: ListPreference) => listPreferenceKey(preference, localStorage.getItem('tid'), 'all');
const USER_TICKETS_LAYOUT = [
  ListPreference.UserTicketsColumnWidths, ListPreference.UserTicketsColumnOrder, ListPreference.UserTicketsSortOrder, ListPreference.UserTicketsHiddenColumns,
];


interface Features {
  useTicketType: boolean;
  useSLA: boolean;
  useTeam: boolean;
  useTicketLifecycle: boolean;
  useTicketTags: boolean;
  useTicketTemplates: boolean;
  useCustomProperties: boolean;
  useGroupChat: boolean;
  useTimeTracking: boolean;
  /** Resolution notes are licensed and switched on, so resolving from the Status cell asks for one. */
  useResolutionNote: boolean;
  resolutionNoteConfig: TikitConfiguration | undefined;
}

interface Props {
  userId: number;
  /** Called after a card opens a ticket, so the side panel on a ticket closes rather than staying over the new one. */
  onTicketOpened?: () => void;
  /** Shown in a ticket's side panel, which is white, rather than on the grey users page. */
  inPanel?: boolean;
}

const api = new platformService();

export const UserTickets = ({ userId, onTicketOpened, inPanel }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const currentState = appState();

  const [features, setFeatures] = useState<Features>(null);
  const [lookups, setLookups] = useState<Lookups>(null);
  const [editLists, setEditLists] = useState<EditLists>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TICKET_PAGINATION.ITEM_PER_PAGE());
  const [sort, setSort, resetSort] = usePersistedSort<ISortOrder>(userTicketsKey(ListPreference.UserTicketsSortOrder), DEFAULT_SORT);

  // Follows the stored settings, so Reset View enables the moment a column is dragged or sorted.
  const canResetView = useListPreferenceFlag(() => USER_TICKETS_LAYOUT.some(preference => readListPreference(userTicketsKey(preference)) != null));

  // Reset View: back to the default widths, column order and sort. Clearing the widths and order makes the
  // grid rebuild itself (ClassDetailList listens). As on the ticket list and My Work, page one is fetched again
  // only if the sort moved. That fetch, a sort click's and Refresh's leave the rows on screen, greyed and frozen
  // (keepRowsRef), rather than the loading skeleton.
  const keepRowsRef = useRef(false);
  // A save in a cell reloads the page with nothing shown at all: the rows stay as they are, not greyed - the cell
  // has its own saving mark, as on the ticket list and My Work.
  const quietRef = useRef(false);
  // Saves in a cell waiting for their reload to be drawn: until then the grid fades the rows it takes out (rowLeave.ts).
  const savesWaiting = useRef<(() => void)[]>([]);
  const finishSaves = () => {
    const waiting = savesWaiting.current;
    savesWaiting.current = [];
    waiting.forEach(done => done());
  };
  useEffect(() => finishSaves, []);
  const onTicketEdited = () => new Promise<void>(resolve => {
    savesWaiting.current.push(resolve);
    quietRef.current = true;
    setRefreshCount(count => count + 1);
  });
  const onResetView = () => {
    if (!canResetView) return;
    writeListPreference(userTicketsKey(ListPreference.UserTicketsColumnWidths), null);
    writeListPreference(userTicketsKey(ListPreference.UserTicketsColumnOrder), null);
    writeListPreference(userTicketsKey(ListPreference.UserTicketsHiddenColumns), null);
    if (sort.orderBy === DEFAULT_SORT.orderBy && sort.orderType === DEFAULT_SORT.orderType) {
      writeListPreference(userTicketsKey(ListPreference.UserTicketsSortOrder), null);
      return;
    }
    keepRowsRef.current = true;
    resetSort();
    setPage(1);
  };
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  // A further page on a phone that failed to load. It is asked for again when the user next scrolls - not at once,
  // and not skipped: the end of the cards is still on screen, so moving on would lose that page and, while the
  // server keeps failing, ask for page after page without end.
  const [appendFailed, setAppendFailed] = useState(false);
  // A page that came back short is the last. Counting cards against the total is not enough once a repeated card
  // is dropped (below): the cards then stay one short of the total, and it would ask past the end for good.
  const [reachedEnd, setReachedEnd] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // Refresh: a new value runs the fetch again with everything else as it is.
  const [refreshCount, setRefreshCount] = useState(0);
  const latestRequest = useRef(0);
  const endOfCardsRef = useRef<HTMLDivElement>(null);
  // Tailwind's md breakpoint, where the user page swaps its tab bar for a dropdown and the ticket list
  // draws cards rather than the grid.
  const isPhone = useWindowWidth() < 768;

  useEffect(() => {
    // A list that fails to load leaves its columns out, not the whole tab.
    const leaveOut = (what: string) => (error: unknown): undefined => {
      console.error(`Error loading the ${what} for the user's tickets`, error);
      return undefined;
    };
    (async () => {
      const [useTicketType, useSLA, useTeam, useTicketLifecycle, useTicketTags, resolutionNotes, resolutionNoteConfig, loaded, useTicketTemplates, customProperties, useGroupChat, useTimeTracking] = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.TicketTypes),
        getCachedFeature(LICENSE_FEATURE.ServiceLevelAgreements),
        getCachedFeature(LICENSE_FEATURE.MultiDepartment),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
        getCachedFeature(LICENSE_FEATURE.TicketTags),
        // Names for the Resolution Category column, only loaded while resolution notes are licensed.
        getCachedFeature(LICENSE_FEATURE.ResolutionNote).then(async licensed => ({
          licensed,
          categories: licensed ? await loadResolutionCategories(currentState).catch(leaveOut('resolution categories')) : undefined,
        })),
        // Read as the ticket list reads them (TicketBoard), for the Status cell.
        api.getSystemSetting(ConfigurationName.Resolution)
          .then(response => ({ ...response.data, IsResolutionNoteEnabled: response.data.IsEnabled }) as TikitConfiguration)
          .catch(() => undefined),
        loadLookups(currentState),
        getCachedFeature(LICENSE_FEATURE.TicketTemplates),
        getCachedFeature(LICENSE_FEATURE.CustomProperties)
          .then(licensed => licensed ? loadCustomProperties(currentState).catch(leaveOut('custom properties')) : undefined),
        getCachedFeature(LICENSE_FEATURE.GroupChat),
        getCachedFeature(LICENSE_FEATURE.TimeTracking),
      ]);
      const resolutionCategory = resolutionNotes.categories;
      setLookups({ ...loaded, ...(customProperties ? { customProperties } : {}), ...(resolutionCategory ? { resolutionCategory } : {}) });
      setFeatures({
        useTicketType, useSLA, useTeam, useTicketLifecycle, useTicketTags, useTicketTemplates, useGroupChat, useTimeTracking,
        useCustomProperties: !!customProperties,
        // The licence and the setting, as the ticket list decides it - names that failed to load do not switch the
        // resolution note off.
        useResolutionNote: resolutionNotes.licensed && !!resolutionNoteConfig?.IsResolutionNoteEnabled,
        resolutionNoteConfig,
      });
    })().catch(error => {
      console.error('Error loading the ticket list settings', error);
      setIsError(true);
      setIsLoading(false);
    });
  }, []);

  // What the grid's edit panels choose from. Loaded once the tab is up and not waited for: until it arrives a panel
  // opens with empty lists, which is rarer than a slower first draw. Desktop only - the phone's cards do not edit.
  useEffect(() => {
    if (isPhone || editLists) return;
    loadEditLists(currentState).then(setEditLists).catch(error => console.error('Error loading the lists for editing', error));
  }, [isPhone]);

  // Crossing between the grid and the cards starts again from the first page. Done here, in the same run, rather
  // than by a separate effect: that one ran after this, so the page the user was on was fetched first and thrown away.
  const pageLayoutIsPhone = useRef(isPhone);
  useEffect(() => {
    if (pageLayoutIsPhone.current !== isPhone) {
      pageLayoutIsPhone.current = isPhone;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    if (!features || !userId) return;
    const request = ++latestRequest.current;
    // A phone loads further pages onto the end of the cards rather than replacing them.
    const appending = isPhone && page > 1;
    const keepRows = keepRowsRef.current && !appending;
    const quiet = quietRef.current && !appending;
    keepRowsRef.current = false;
    quietRef.current = false;
    if (quiet) { /* the rows stay as they are */ }
    else if (appending) setIsAppending(true);
    else if (keepRows) setIsRefreshing(true);
    else setIsLoading(true);
    setIsError(false);
    setAppendFailed(false);

    const requestedSize = cardListPageSize(isPhone, pageSize);

    (async () => {
      const { data } = await api.getTicketList(buildUserTicketsQuery({
        userId,
        page,
        pageSize: requestedSize,
        orderByField: TICKET_LIST_COLUMN_IDS[sort.orderBy] ?? getCustomPropertyQueryValue(lookups?.customProperties ?? [], TICKET_LIST_COLUMN_IDS, sort.orderBy),
        descending: sort.orderType === EnumSortOrderType.desc,
        search: appliedSearch,
        tags: features.useTicketTags,
        template: features.useTicketTemplates,
        resolution: features.useResolutionNote,
        groupChat: features.useGroupChat,
      }));
      const results: Ticket[] = data.value;
      const slaRows = quiet ? keepUnchangedSlas(results, tickets, ticket => ticket.ModifiedDate) : results;
      await Promise.all([
        attachTicketUsers(results),
        features.useSLA ? attachTicketSlas(slaRows).catch(error => console.error('Error attaching SLAs', error)) : Promise.resolve(),
      ]);
      if (request !== latestRequest.current) return;
      // A ticket raised between two pages shifts the next page down one, repeating the last card; it is dropped.
      setTickets(prev => appending ? appendPage(prev, results, ticket => ticket.Id) : results);
      setTotalCount(data['@odata.count'] ?? results.length);
      setReachedEnd(results.length < requestedSize);
      setIsLoading(false);
      setIsRefreshing(false);
      setIsAppending(false);
      finishSaves();
    })().catch(error => {
      if (request !== latestRequest.current) return;
      console.error('Error fetching the user\'s tickets', error);
      // A failed further page leaves the cards already loaded in place.
      if (appending) setAppendFailed(true);
      else setIsError(true);
      setIsLoading(false);
      setIsRefreshing(false);
      setIsAppending(false);
      finishSaves();
    });
  }, [features, userId, page, pageSize, sort, isPhone, appliedSearch, refreshCount]);

  // The search box: the ticket list filter's text box. On desktop it searches once typing pauses, or on Enter;
  // on a phone on every keystroke, as the ticket list's own phone search does.
  const applySearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === appliedSearch) return;
    setAppliedSearch(trimmed);
    setPage(1);
  };

  useEffect(() => {
    const pause = window.setTimeout(() => applySearch(search), isPhone ? 0 : 400);
    return () => window.clearTimeout(pause);
  }, [search]);

  // On a phone there is no pager: as the end of the cards comes near, the next page loads onto them, as on
  // the ticket list. A marker after the last card is watched against the screen, so it works whether the
  // user page or the ticket's side panel is what scrolls - and keeps loading while the cards do not yet fill
  // the screen, since the effect runs again after every page.
  const hasMore = !reachedEnd && tickets.length < totalCount;
  useEffect(() => {
    const marker = endOfCardsRef.current;
    if (!isPhone || !marker || !hasMore || isLoading || isAppending) return;
    // The same page again (Refresh's trigger keeps the page), once the user next does something.
    if (appendFailed) return onNextUserGesture(() => setRefreshCount(count => count + 1));
    const observer = new IntersectionObserver(
      entries => { if (entries.some(entry => entry.isIntersecting)) setPage(prev => prev + 1); },
      // At the bottom, as every phone card list loads its next page (the ticket list, My Work's lanes).
      { rootMargin: '0px' }
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [isPhone, hasMore, isLoading, isAppending, appendFailed, tickets.length]);

  // The grid reads statuses and the rest from app state. Those are given to it here rather than written
  // to app state, which re-renders the whole app - and team and group names come back on each ticket.
  const gridState = useMemo<AppStateValue>(
    () => ({
      ...currentState, ...lookups, ...editLists,
      teams: editLists?.teams ?? currentState.teams ?? [],
      supportgroups: editLists?.supportgroups ?? currentState.supportgroups ?? [],
      resolutionCategory: lookups?.resolutionCategory ?? currentState.resolutionCategory ?? []
    }),
    [currentState, lookups, editLists]
  );

  const columns = useMemo(() => withCustomPropertyColumns(lookups?.customProperties), [lookups]);

  const rows = useMemo(
    () => tickets.map((ticket, i) => toTicketRow(ticket, i, gridState, t)),
    [tickets, gridState]
  );

  // The phone draws the ticket list's own mobile cards. Team and group names come back on each ticket,
  // and win over the lookup, which reads app state the user page never fills.
  const cards = useMemo(
    () => tickets.map(ticket => ({
      ...ticket,
      ...getMobileTicketProperties(ticket, gridState),
      Team: ticket.TeamId == null ? '' : ticket.Team,
      SupportGroup: ticket.SupportGroupId == null ? '' : ticket.SupportGroup,
    })),
    [tickets, gridState]
  );

  // On a phone a card opens the ticket in place, as the ticket list's cards do. (On desktop the grid's links
  // open a new tab instead, so the user page stays where it is.)
  // The side panel closes once the ticket has actually opened, not when the card is tapped: the page underneath
  // may ask before leaving (an unsent comment), and if the user stays, the route never changes and the panel
  // stays as it was.
  const openingTicketPath = useRef<string>(null);
  const openTicket = (ticketId: number) => {
    openingTicketPath.current = `/tickets/${ticketId}`;
    history.push(openingTicketPath.current);
  };
  useEffect(() => {
    if (!openingTicketPath.current) return;
    const opened = location.pathname === openingTicketPath.current;
    openingTicketPath.current = null;
    if (opened) onTicketOpened?.();
  }, [location.key]);

  const onColumnClick = (column: IColumn) => {
    const columnKey = Number(column.key);
    if (NON_SORTABLE_COLUMNS.includes(columnKey)) return;
    keepRowsRef.current = true;
    setSort({
      orderBy: columnKey as ISortOrderBy,
      orderType: column.isSortedDescending ? EnumSortOrderType.asc : EnumSortOrderType.desc,
    });
    setPage(1);
  };

  // The column the list was sorted by was removed: back to the default sort, page one fetched with the rows left on screen.
  const onSortedColumnRemoved = () => {
    keepRowsRef.current = true;
    resetSort();
    setPage(1);
  };

  const moveToPage = (action: string) => {
    if (action === 'next') setPage(prev => prev + 1);
    else if (action === 'previous') setPage(prev => Math.max(prev - 1, 1));
    else setPage(parseInt(action) || 1);
  };

  const onPageSizeChanged = (count: number) => {
    setPageSize(count);
    setPage(1);
  };

  const errorState = (
    <EmptyData
      headerText={t('users.tickets.error')}
      subheaderText=''
      SVGIcon={<ErrorIcon size='largest' />} />
  );

  const noTickets = (
    <EmptyData
      headerText={t('users.tickets.empty-header')}
      subheaderText={t('users.tickets.empty-subheader')}
      SVGIcon={<EmptySVG height={200} width={200} />} />
  );

  const noMatches = (
    <EmptyData
      headerText={t('ticket-details.edit-ticket-view.empty.header-text')}
      subheaderText={t('common.try-a-different-search')}
      SVGIcon={<FlashlightSVG width={200} height={200} className='pb-2' />} />
  );

  // The ticket list's own phone search box: an x appears once there is text and clears it. Full width above
  // the cards on a phone; top right on desktop, as on the users and service catalog pages. Kept mounted
  // through loading and empty results, so the box never loses focus while a search runs.
  // Inverted (white) on the grey users page. In the white side panel that washed into the background, so the
  // plain filled style there. In the dark panel both styles sit within a few shades of the panel (#242424 in
  // main.css), so the field takes a lighter shade of the theme's own: background5 (#333 in Tikit's dark theme),
  // chosen over background4 (#3d3d3d), which stood out more than the ticket page's own Activity search does.
  const searchRow = (
    <ProviderConsumer render={globalTheme => {
      const isDark = globalTheme.siteVariables.theme !== themeNames.Default;
      const { colorScheme } = globalTheme.siteVariables;
      const input = (
        <Input
          clearable
          fluid
          inverted={!inPanel || isDark}
          variables={inPanel && isDark ? { backgroundColorInverted: colorScheme.default.background5 } : undefined}
          className={isPhone ? `${AppCSS.searchInput} w-full flex` : undefined}
          icon={<SearchIcon />}
          placeholder={t('ticket.child-filter.search-request')}
          value={search}
          onChange={(_e, data) => setSearch(data?.value ?? '')}
          onKeyDown={e => { if (e.key === 'Enter') applySearch(search); }}
        />
      );
      return isPhone
        ? <div className='pt-2'>{input}</div>
        : (
          // Level with the table's edges; its 10px padding starts the label where the tab names do (measured).
          <ListViewBar
            colorScheme={colorScheme}
            label={t('users.tickets.all-tickets')}
            canResetView={canResetView}
            onResetView={onResetView}
            onRefresh={() => { keepRowsRef.current = true; setRefreshCount(count => count + 1); }}
            search={<div style={{ width: '320px' }}>{input}</div>}
            refreshing={isRefreshing}
            style={{ margin: '8px 10px' }}
          />
        );
    }} />
  );

  if (isPhone) {
    let phoneBody: React.ReactNode;
    if (isError) phoneBody = errorState;
    // As the ticket list does: card placeholders on a phone, where the table's would be squeezed into a
    // few pixels per column.
    else if (isLoading) phoneBody = (
      // user-ticket-skeleton (main.css): each placeholder outlined and clipped like the cards it stands for.
      <div className='py-3'>
        {[0, 1, 2].map(i => <div key={i} className='user-ticket-skeleton'><TicketCardSkeleton /></div>)}
      </div>
    );
    else if (cards.length === 0) phoneBody = appliedSearch ? noMatches : noTickets;
    else phoneBody = (
      <>
        <div className='py-1'>
          {cards.map(ticket => (
            <div key={ticket.Id} className='my-2' onClick={() => openTicket(ticket.Id)} style={{ marginBottom: '0.2rem', cursor: 'pointer' }}>
              <TicketDetailCard isBoardMobileView={true} ticketData={ticket} idColorPurple />
            </div>
          ))}
        </div>
        <div ref={endOfCardsRef} />
        {isAppending && <Loader />}
      </>
    );
    // The tab sits in the user page's 20px padding; the cards and search reach 8px into it, so they keep the
    // same 12px side gutter as the ticket list's and My Work's phone cards.
    return (
      <div style={{ margin: '0 -8px' }}>
        {searchRow}
        {phoneBody}
      </div>
    );
  }

  const hasRows = !isError && !isLoading && rows.length > 0;
  const placeholder = () => {
    if (isError) return errorState;
    if (isLoading) return <SkeletonTable />;
    return appliedSearch ? noMatches : noTickets;
  };

  // Fills the tab area the user page sizes on desktop, so the grid scrolls both ways inside it as the ticket
  // list does, with the search above it and the pager kept below.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {searchRow}
      <div className={isRefreshing ? 'list-rows-refreshing' : undefined} style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
        {hasRows ? (
          <ClassDetailList
            standalone
            // Typed with the global Ticket, but what the grid draws is the mapped row - TicketListBoard
            // passes the same rows untyped.
            tickets={rows as unknown as Ticket[]}
            onTicketsSelected={() => { }}
            currentState={gridState}
            useTicketType={features.useTicketType}
            useTeam={features.useTeam}
            useTicketLifecycle={features.useTicketLifecycle}
            useSLA={features.useSLA}
            useGroupChat={features.useGroupChat}
            useCustomProperties={features.useCustomProperties}
            useTimeTracking={features.useTimeTracking}
            useTicketTags={features.useTicketTags}
            useTicketTemplates={features.useTicketTemplates}
            selectedOrderedColumns={columns}
            sortOrderApplied={sort}
            clickColumnCallback={onColumnClick}
            columnWidthsKey={userTicketsKey(ListPreference.UserTicketsColumnWidths)}
            columnOrderKey={userTicketsKey(ListPreference.UserTicketsColumnOrder)}
            hiddenColumnsKey={userTicketsKey(ListPreference.UserTicketsHiddenColumns)}
            onSortedColumnRemoved={onSortedColumnRemoved}
            // Desktop only: the phone draws cards (above), which do not edit.
            useResolutionNote={features.useResolutionNote}
            resolutionNoteConfig={features.resolutionNoteConfig}
            onTicketEdited={onTicketEdited}
          />
        ) : placeholder()}
      </div>
      {hasRows && (
        <div className='pr-4 h-9 flex items-center'>
          <ResultCount count={totalCount} />
          <TablePagination currentPage={page} totalCount={totalCount} moveToPage={moveToPage} itemsPerPage={pageSize} itemsPerPageChanged={onPageSizeChanged} />
        </div>
      )}
    </div>
  );
};

interface Lookups {
  ticketStatus: TicketProps[];
  priority: TicketProps[];
  categories: TicketProps[];
  ticketTypes: TicketProps[];
  source: TicketProps[];
  /** The templates this agent may use (scoped) - the Template column names only these. */
  ticketTemplates?: AppStateValue['ticketTemplates'];
  /** Only when Custom Properties is licensed. */
  customProperties?: ICustomProperty[];
  /** Only when Resolution Notes is licensed. */
  resolutionCategory?: TicketProps[];
}

interface EditLists {
  teams: AppStateValue['teams'];
  supportgroups: AppStateValue['supportgroups'];
  usersInRole: AppStateValue['usersInRole'];
  taskStatus: AppStateValue['taskStatus'];
  tags: AppStateValue['tags'];
}

const has = (list: unknown[] | undefined) => (list?.length ?? 0) > 0;

// The lists the ticket list's loadFilters puts in app state for its edit panels - teams with their members from
// Graph, groups, agents, task statuses (Status warns about open tasks) and tags - and only those the app lacks.
const loadEditLists = async (state: AppStateValue): Promise<EditLists> => {
  const [teams, supportgroups, usersInRole, taskStatus, tags] = await Promise.all([
    has(state.teams) ? state.teams : api.getTeamsSafe(FILTERS_STRINGS.TICKET_TEAMS),
    has(state.supportgroups) ? state.supportgroups : api.getSupportGroups(FILTERS_STRINGS.TICKET_GROUPS).then(response => response.data.value),
    has(state.usersInRole) ? state.usersInRole : api.getUsersInRole().then(response => JSON.parse(response.data.value)),
    has(state.taskStatus) ? state.taskStatus : api.getTaskStatus(`${FILTERS_STRINGS.FILTERS_URL_PARAMS} and IsPowerAutomateStatus eq false`),
    has(state.tags) ? state.tags : api.getTags(FILTERS_STRINGS.DELETE_FALSE),
  ]);
  const withMembers = has(state.teams) ? teams : await getExpandedTeamMembers(new graphService(), teams, usersInRole).catch(() => teams);
  return { teams: withMembers, supportgroups, usersInRole, taskStatus, tags };
};

// Only the lookups the grid draws names from, and only if the app has not loaded them already. The
// ticket list's loadFilters would do it too, but it fetches seventeen lists plus every team's members
// and writes them all to app state, which re-renders the whole user page.
const loadTemplates = async (state: AppStateValue) => state.ticketTemplates ??
  api.getTicketTemplates(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}&$select=Id,Name&$orderby=Name asc`).then(r => r.data.value).catch(() => undefined);

// As the ticket list loads them (loadFilters), with each list property's choices, which its cells name.
const loadCustomProperties = async (state: AppStateValue): Promise<ICustomProperty[]> => has(state.customProperties) ? state.customProperties :
  api.getCustomProperties(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}&${FILTERS_STRINGS.CUSTOM_PROPERTY_LIST}&$orderby=Order`).then(response => response.data.value);

const loadResolutionCategories = async (state: AppStateValue): Promise<TicketProps[]> => has(state.resolutionCategory) ? state.resolutionCategory :
  api.getResolutionCategories(FILTERS_STRINGS.ARCHIVE_FALSE).then(response => response.data.value);

const loadLookups = async (state: AppStateValue): Promise<Lookups> => {
  if ([state.ticketStatus, state.priority, state.categories, state.ticketTypes, state.source].every(list => (list?.length ?? 0) > 0))
    return { ticketStatus: state.ticketStatus, priority: state.priority, categories: state.categories, ticketTypes: state.ticketTypes, source: state.source, ticketTemplates: await loadTemplates(state) };

  // With their archived entries, as loadFilters loads them: a ticket can still hold one (archivedLookups.ts).
  const [statuses, priorities, categories, ticketTypes, sources] = await Promise.all([
    api.getStatus(FILTERS_STRINGS.NOT_DELETED),
    api.getPriority(FILTERS_STRINGS.NOT_DELETED),
    api.getCategories(FILTERS_STRINGS.NOT_DELETED),
    api.getTicketTypesSafe(FILTERS_STRINGS.NOT_DELETED),
    api.getSource(FILTERS_STRINGS.NOT_DELETED),
  ]);
  return {
    ticketStatus: liveWithArchived(statuses.data.value), priority: liveWithArchived(priorities.data.value),
    categories: liveWithArchived(categories.data.value), ticketTypes: liveWithArchived(ticketTypes), source: liveWithArchived(sources.data.value),
    ticketTemplates: await loadTemplates(state)
  };
};
